/**
 * Phase 18 — inline "Add a photo" button.
 *
 * Rendered below an assistant message whose `photo_request` is true
 * (Stella emitted `[[ADD_PHOTO]]`). Wraps a hidden file input — same
 * upload path as the workspace `PhotoUploadButton`, just styled to sit
 * inline in the chat like the Phase 14 MeasureButton.
 */

import { useRef, useState } from "react";

import { uploadPhotoFromFile } from "@starz/shared/photos/upload";
import type { Photo } from "@starz/shared/api/types.gen";

interface Props {
  estimateId: string;
  /** Bubbles the uploaded photo up so the workspace gallery + render
   * flow can pick it up without a reload. */
  onUploaded?: (photo: Photo) => void;
  disabled?: boolean;
}

export function AddPhotoButton({ estimateId, onUploaded, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    const result = await uploadPhotoFromFile({ estimateId, file: files[0] });
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    if (!result.ok) {
      setError(result.error ?? "Upload failed.");
      return;
    }
    setDone(true);
    if (result.photo) onUploaded?.(result.photo);
  }

  if (done) {
    return (
      <div className="ml-1 mt-1 text-sm text-emerald-700">
        ✓ Photo added — ask me to render it.
      </div>
    );
  }

  return (
    <div className="ml-1 mt-1 space-y-1">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || busy}
        className="inline-flex items-center gap-2 rounded-full bg-starz-navy px-4 py-2 text-sm font-semibold text-starz-off-white disabled:opacity-50"
      >
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
        {busy ? "Uploading…" : "Add a photo"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        capture="environment"
        onChange={(e) => void handleFile(e.target.files)}
        className="hidden"
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}
